import { availableNodes } from "../meta/run";
import { RUN_COLUMNS, type NodeKind, type NodeModifier, type RunNode, type RunState } from "../meta/run-types";
import { fieldModById } from "../meta/field-mods";
import { THEATERS, nodeDescriptor } from "../mission/variants";

// The Operation map: the run's node DAG rendered as columns with SVG edges,
// Slay-the-Spire style. Pure renderer — node picks and abandonment go back
// through callbacks; run rules live in meta/run.ts.

const KIND_ICON: Record<NodeKind, string> = {
  combat: "◆",
  elite: "▲",
  defend: "▣",
  destroy: "✸",
  ambush: "➤",
  delay: "⊘",
  sead: "◎",
  extraction: "➥",
  rescue: "✛",
  resupply: "✚",
  final: "⬢",
};

const MODIFIER_LABEL: Record<NodeModifier, string> = {
  storm: "STORM",
  escalating: "ESCALATING",
  "aa-dense": "AA DENSE",
  "heavy-armor": "ARMOR",
};

const KIND_LABEL: Record<NodeKind, string> = {
  combat: "ESCORT",
  elite: "HARDENED",
  defend: "DEFEND",
  destroy: "FIRE MSN",
  ambush: "AMBUSH",
  delay: "DELAY",
  sead: "SEAD",
  extraction: "EXFIL",
  rescue: "RESCUE",
  resupply: "RESUPPLY",
  final: "FINALE",
};

// Layout space for nodes and edges (viewBox units; CSS scales it)
const VIEW_W = 1000;
const VIEW_H = 440;

function nodeXY(node: RunNode, colSizes: number[]): { x: number; y: number } {
  const x = 70 + (node.col / (RUN_COLUMNS - 1)) * (VIEW_W - 140);
  const rows = colSizes[node.col] ?? 1;
  const y = VIEW_H / 2 + (node.row - (rows - 1) / 2) * 120;
  return { x, y };
}

export class RunMapScreen {
  private readonly el: HTMLElement;
  private run: RunState | null = null;
  private banner = "";

  constructor(private readonly callbacks: {
    onEnterNode: (node: RunNode) => void;
    onAbandon: () => void;
  }) {
    this.el = document.createElement("div");
    this.el.id = "run-map";
    this.el.className = "screen-overlay screen-overlay--stacked";
    this.el.style.zIndex = "350";
    document.body.appendChild(this.el);
    this.el.addEventListener("click", this.handleClick);
  }

  setRun(run: RunState, banner = ""): void {
    this.run = run;
    this.banner = banner;
    this.render();
  }

  private render(): void {
    const run = this.run;
    if (!run) return;

    const colSizes: number[] = [];
    for (const node of run.nodes) colSizes[node.col] = Math.max(colSizes[node.col] ?? 0, node.row + 1);

    const open = new Set(availableNodes(run).map((n) => n.id));
    const cleared = new Set(run.clearedNodeIds);

    const edges = run.nodes.flatMap((node) => {
      const from = nodeXY(node, colSizes);
      return node.next.map((id) => {
        const target = run.nodes.find((n) => n.id === id)!;
        const to = nodeXY(target, colSizes);
        const active = (cleared.has(node.id) || node.id === run.currentNodeId) && open.has(id);
        return `<line x1="${from.x}" y1="${from.y}" x2="${to.x}" y2="${to.y}" class="run-edge${active ? " run-edge--active" : ""}" />`;
      });
    }).join("");

    const nodesHtml = run.nodes.map((node) => {
      const { x, y } = nodeXY(node, colSizes);
      const state = cleared.has(node.id)
        ? "cleared"
        : open.has(node.id)
          ? "open"
          : "locked";
      const here = node.id === run.currentNodeId ? " run-node--here" : "";
      const desc = nodeDescriptor(node, run.theater);
      const nightTag = node.night ? `<div class="run-node__night">NIGHT</div>` : "";
      const modTag = node.modifier ? `<div class="run-node__night run-node__modifier">${MODIFIER_LABEL[node.modifier]}</div>` : "";
      return `
        <div class="run-node run-node--${state} run-node--${node.kind}${here}"
             style="left:${(x / VIEW_W) * 100}%;top:${(y / VIEW_H) * 100}%"
             data-node="${node.id}" title="${desc.blurb}">
          <div class="run-node__icon">${KIND_ICON[node.kind]}</div>
          <div class="run-node__kind">${KIND_LABEL[node.kind]}</div>
          <div class="run-node__sub">${desc.name}</div>
          ${nightTag}
          ${modTag}
        </div>`;
    }).join("");

    const pad = (n: number) => n.toString().padStart(3, "0");
    const rosterNames = run.roster
      .map((name) => `<span class="run-operator">${name}</span>`)
      .join("<span class='run-operator-sep'>·</span>");
    const fallenNames = run.fallen.length
      ? `<span class="run-operator-sep">|</span> KIA: ${run.fallen
          .map((name) => `<span class="run-operator run-operator--kia">${name}</span>`)
          .join(" ")}`
      : "";

    const modChips = run.modIds.length
      ? run.modIds.map((id) => {
          const mod = fieldModById(id);
          return `<span class="run-mod-chip run-mod-chip--${mod?.rarity ?? "common"}" title="${mod?.desc ?? ""}">${mod?.name ?? id}</span>`;
        }).join("")
      : `<span class="run-mod-chip run-mod-chip--empty">NO FIELD MODS YET</span>`;

    const bannerHtml = this.banner ? `<div class="run-banner">▸ ${this.banner}</div>` : "";

    this.el.innerHTML = `
      <div class="screen-kicker">TOP SECRET // NOFORN &nbsp;—&nbsp; SERIAL ${run.seed.toString(16).toUpperCase().padStart(8, "0")}</div>
      <div class="screen-title screen-title--compact">${THEATERS[run.theater].codename}</div>
      ${bannerHtml}
      <div class="run-status-row">
        <div class="run-status">ALPHA &nbsp;${rosterNames} ${fallenNames}</div>
        <div class="run-status">OPS &nbsp;<span class="run-status__value">${pad(run.opsEarned)}</span></div>
        <div class="run-status">KILLS &nbsp;<span class="run-status__value">${pad(run.kills)}</span></div>
        ${run.escalation > 0 ? `<div class="run-status run-status--alert">REDFOR ALERT +${run.escalation}</div>` : ""}
      </div>
      <div class="run-tree">
        <svg class="run-tree__edges" viewBox="0 0 ${VIEW_W} ${VIEW_H}" preserveAspectRatio="none">${edges}</svg>
        ${nodesHtml}
      </div>
      <div class="run-mods-row">${modChips}</div>
      <div class="screen-actions" style="margin-top:10px">
        <button id="run-abandon" class="screen-button screen-button--muted screen-button--small">ABANDON OPERATION</button>
      </div>
    `;
  }

  show(): void {
    this.render();
    this.el.classList.add("is-visible");
  }

  hide(): void {
    this.el.classList.remove("is-visible");
  }

  private handleClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement | null;
    if (!target || !this.run) return;

    if (target.closest("#run-abandon")) {
      this.callbacks.onAbandon();
      return;
    }

    const nodeId = target.closest<HTMLElement>("[data-node]")?.dataset["node"];
    if (!nodeId) return;
    const node = this.run.nodes.find((n) => n.id === nodeId);
    if (!node || !availableNodes(this.run).some((n) => n.id === nodeId)) return;
    this.callbacks.onEnterNode(node);
  };
}
